import { Query } from '@flakiness/server/common/fql/query.js';
import { Ranges } from '@flakiness/server/common/ranges.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { isWebUrl } from '../utils.js';
import { highlight } from './hl-span.js';

function wholeRange(text: string): Ranges.Ranges<number> | undefined {
  return text.length ? [0, text.length - 1] as Ranges.Ranges<number> : undefined;
}

@customElement('test-annotation')
class TestAnnotation extends LitElement {
  @property({ attribute: false }) annotation?: WireTypes.Annotation;
  @property({ attribute: false }) fql?: Query;

  render() {
    if (!this.annotation)
      return nothing;
    const hl = this.fql?.highlightAnnotation(this.annotation);
    if (this.annotation.type === 'owner') {
      const desc = this.annotation.description ?? '';
      let descHl = hl?.get(desc);
      // The owner pill hides the type and shows only the description, so a
      // filter matching the type ("owner") would leave no visible highlight.
      // Highlight the whole description instead so the match still reads.
      if (!descHl?.length && hl?.get(this.annotation.type)?.length)
        descHl = wholeRange(desc);
      return html`<sl-tag pill size=small variant=neutral>${highlight(desc, descHl)}</sl-tag>`;
    }
    let type: string | undefined;
    if (this.annotation.type === 'skip')
      type = 'warning';
    else if (this.annotation.type === 'fail')
      type = 'danger';
    else if (this.annotation.type === 'fixme')
      type = 'danger';
    else if (this.annotation.type === 'slow')
      type = 'neutral';
    const descHl = this.annotation.description ? hl?.get(this.annotation.description) : undefined;
    let typeHl = hl?.get(this.annotation.type);
    // When the filter matches only inside the description, highlight the whole
    // type pill so the annotation still reads as matching the filter.
    if (!typeHl?.length && descHl?.length)
      typeHl = wholeRange(this.annotation.type);
    let textRender = highlight(this.annotation.type, typeHl);
    if (this.annotation.description && isWebUrl(this.annotation.description))
      textRender = html`<a-ext href=${this.annotation.description}>${textRender}</a-ext>`;
    const tag = html`
      <span class=${classMap({
        pill: true,
        [type ?? 'neutral']: true,
      })}>${textRender}</span>
    `;
    if (!this.annotation.description)
      return tag;
    return html`
      <sl-tooltip>
        <div slot=content>${highlight(this.annotation.description, descHl)}</div>
        ${tag}
      </sl-tooltip>
    `;
  }

  static styles = [css`
    mark {
      background-color: var(--fk-color-highlight);
      color: black;
    }

    sl-tooltip {
      --max-width: 50rem;

      div[slot=content] {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: pre;
      }
    }

    .pill {
      display: inline-block;
      font-size: var(--sl-font-size-x-small);
      background: var(--sl-color-blue-100);
      border-radius: var(--sl-border-radius-pill);
      padding: var(--sl-spacing-3x-small) var(--sl-spacing-x-small);
      color: var(--sl-color-neutral-700);
    }
  `];
}
